import { Alert, Descriptions, Form, Input, Modal, Space, Typography } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { VOUCHER_REASON_MAX, voidVoucher, type Voucher } from './voucherApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface VoucherVoidModalProps {
  /** `null` — oyna yopiq */
  voucher: Voucher | null
  onClose: () => void
}

interface FormValues {
  reason: string
}

/**
 * BITTA kartani bekor qilish — QAYTARIB BO'LMAYDIGAN amal.
 *
 * ⚠️ Ikki qorovul, uchinchisi ATAYLAB yo'q:
 *
 * 1. **Oqibat proza bilan yozilgan**, "Ishonchingiz komilmi?" emas: karta
 *    o'lik bo'ladi va sotilgan bo'lsa pul o'z-o'zidan qaytmaydi.
 * 2. **Sabab MAJBURIY** (`CancelPaymentModal` naqshi). Bekor qilingan karta
 *    hisobotdan chiqmaydi (`QARORLAR.md` §20.8) va "bu nega kuydi?"
 *    savoliga javob faqat audit yozuvidagi shu maydonda qoladi — keyin
 *    qo'shib bo'lmaydi.
 * 3. **Kod terib tasdiqlash YO'Q** (`NasDeleteModal`/`CancelPaymentModal` da
 *    bor). Sabab: u yerda terish kerak bo'lgan qiymat operator YODIDA
 *    turadigan nom yoki login, bu yerda esa 10–16 belgilik TASODIFIY kod.
 *    Uni ko'chirib qo'yish bir bosishlik ish, ya'ni qorovul emas, marosim
 *    bo'lardi. Noto'g'ri qatorni bosishdan himoya boshqacha qilingan: kod
 *    oynaning ichida ochiq ko'rsatiladi va operator uni qatordagisi bilan
 *    solishtiradi.
 *
 * ⚠️ Kesh IKKI kalitda bekor qilinadi: kartalar ro'yxati (holat o'zgardi)
 * va partiyalar ro'yxati (`VoucherBatch.counts` dagi sanoq siljidi). Bittasini
 * unutish "bekor qildim, lekin sahifada hali ham 'Chop etilgan: 25'" degan
 * ziddiyatni ekranda qoldirardi.
 */
export function VoucherVoidModal({ voucher, onClose }: VoucherVoidModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  const voidCard = useMutation({
    mutationFn: (values: { id: string; reason: string }) => voidVoucher(values.id, values.reason),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.vouchers.codes(updated.batchId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.vouchers.batches })
      form.resetFields()
      onClose()
    },
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin, keyingi kartaga o'tiladi */
  const close = () => {
    form.resetFields()
    voidCard.reset()
    onClose()
  }

  return (
    <Modal
      open={voucher !== null}
      title={t('voucher.voidTitle')}
      okText={t('voucher.void')}
      cancelText={t('common.cancel')}
      okButtonProps={{ danger: true }}
      confirmLoading={voidCard.isPending}
      onCancel={close}
      destroyOnHidden
      onOk={() =>
        submitOnValid(form, (values) => {
          if (voucher === null) return
          voidCard.mutate({ id: voucher.id, reason: values.reason.trim() })
        })
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="warning"
          showIcon
          message={t('voucher.voidWarnTitle')}
          description={t('voucher.voidWarnText')}
        />

        {voucher ? (
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label={t('voucher.code')}>
              <Typography.Text code>{voucher.code}</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('subscribers.columns.status')}>
              {t(`voucherState.${voucher.state}`)}
            </Descriptions.Item>
          </Descriptions>
        ) : null}

        {/* 409/404 shu yerda qoladi — oyna ochiq, ya'ni operator xabarni
            amal tugmasi bilan bir ekranda ko'radi */}
        {voidCard.isError ? (
          <Alert type="error" showIcon message={translateError(voidCard.error, t)} />
        ) : null}

        <Form form={form} layout="vertical" requiredMark preserve={false}>
          <Form.Item
            name="reason"
            label={t('actions.reason')}
            extra={t('voucher.voidReasonHint')}
            rules={[
              { required: true, whitespace: true, message: t('voucher.voidReasonRequired') },
              { max: VOUCHER_REASON_MAX, message: t('voucher.voidReasonTooLong') },
            ]}
          >
            <Input.TextArea rows={3} maxLength={VOUCHER_REASON_MAX} showCount autoFocus />
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  )
}
